// novem atom — ratio meter bar (NovemHR-ekranlar port).
//
// Promotes the two screen-local copies: operator-console's MeterBar
// (panel/Shell.jsx HealthBar — SLO budget / usage) and customer-web's
// CapMeter (leave-extra.jsx usage bar — remaining-of-cap). A thin
// horizontal bar whose fill width is a clamped 0..1 ratio, tinted by tone.
//
// API (per the two call sites):
//   * ratio: 0..1 (clamped) — fill share.
//   * tone: accent | success | warning | danger | neutral (leave used
//     accent; health used success/warning/danger).
//   * label: accessible description of what the bar shows ("98% bütçe
//     kaldı"). Drives the role="img" name. The VISIBLE caption beneath a
//     meter stays a screen concern (layout-specific) — not baked in here.
//
// Accessibility: role="img" + aria-label so the bar is announced as a
// single labeled graphic (a bare colored div is meaningless to AT). When
// no label is given it falls back to role="presentation" (pure decoration
// alongside a visible number the host already renders).

import { forwardRef, type HTMLAttributes } from "react";

import { cx } from "../lib/cx.ts";

export type MeterBarTone = "accent" | "success" | "warning" | "danger" | "neutral";

export interface MeterBarProps extends Omit<HTMLAttributes<HTMLDivElement>, "role"> {
  /** Fill ratio, 0..1 (clamped). */
  readonly ratio: number;
  /** Fill tint. Defaults to 'accent'. */
  readonly tone?: MeterBarTone;
  /** Accessible description (e.g. "98% bütçe kaldı"). Sets role="img" + name. */
  readonly label?: string;
}

export const MeterBar = forwardRef<HTMLDivElement, MeterBarProps>(
  ({ ratio, tone = "accent", label, className, ...rest }, ref) => {
    const pct = Math.min(100, Math.max(0, ratio * 100));
    return (
      <div
        ref={ref}
        // A labeled bar is a graphic; an unlabeled one is decoration next
        // to a host-rendered number (don't announce an empty img).
        role={label !== undefined ? "img" : "presentation"}
        aria-label={label}
        data-component="meter-bar"
        data-tone={tone}
        className={cx("nai-meter-bar", className)}
        {...rest}
      >
        <div className="nai-meter-bar-fill" style={{ inlineSize: `${pct}%` }} />
      </div>
    );
  },
);
MeterBar.displayName = "MeterBar";
